/**
 * Idempotence — guide §4.7.
 *
 * Les endpoints qui engagent de l'argent ou changent un état sensible
 * exigent l'en-tête `Idempotency-Key` : une valeur unique PAR TENTATIVE
 * MÉTIER. Règles serveur :
 * - même clé + même corps + traitement terminé → réponse d'origine rejouée ;
 * - même clé + corps différent → 422 ;
 * - même clé + traitement en cours → 409 (attendre puis relire) ;
 * - clés conservées 24 h, cloisonnées par utilisateur et par endpoint.
 *
 * Usage type dans un formulaire de submit :
 *   const scope = createIdempotencyScope();
 *   // au submit (et à chaque retry automatique de la MÊME tentative) :
 *   mutateData({ url, reqBody, method: "POST", idempotencyKey: scope.key });
 *   // quand l'utilisateur corrige le formulaire et retente (nouvelle
 *   // tentative métier) : scope.renew() AVANT de relancer.
 */

/** Génère une clé d'idempotence (UUID v4). */
export const createIdempotencyKey = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Contexte non sécurisé (http hors localhost) : repli non cryptographique,
  // suffisant pour de l'idempotence (unicité pratique, pas de la sécurité).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export interface IdempotencyScope {
  /** Clé courante — stable pour toute la tentative métier (retry inclus). */
  readonly key: string;
  /** Nouvelle tentative métier : régénère la clé (appeler AVANT le retry). */
  renew: () => void;
}

/** Crée un scope d'idempotence : une clé par tentative métier. */
export const createIdempotencyScope = (): IdempotencyScope => {
  let current = createIdempotencyKey();
  return {
    get key() {
      return current;
    },
    renew() {
      current = createIdempotencyKey();
    },
  };
};

const STORAGE_PREFIX = "fp.idempotency.";

/** Empreinte stable d'un corps JSON (clés triées) pour lier une clé à une tentative. */
export const fingerprintBody = (body: unknown): string => {
  const stable = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.keys(value as object)
          .sort()
          .map((k) => [k, stable((value as Record<string, unknown>)[k])])
      );
    }
    return value;
  };
  return JSON.stringify(stable(body));
};

const storageKeyFor = (scopeId: string, fingerprint: string): string =>
  `${STORAGE_PREFIX}${scopeId}:${fingerprint}`;

/**
 * Récupère (ou crée) la clé d'idempotence d'une tentative métier.
 * Persistée en `sessionStorage` : survit à un reload / coupure réseau dans
 * le même onglet (guide §4.7, Phase 10.2) ; une nouvelle tentative (corps
 * différent) produit une empreinte différente donc une nouvelle clé.
 */
export const getOrCreatePersistedIdempotencyKey = (
  scopeId: string,
  fingerprint: string
): string => {
  const storageKey = storageKeyFor(scopeId, fingerprint);
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const key = createIdempotencyKey();
    sessionStorage.setItem(storageKey, key);
    return key;
  } catch {
    return createIdempotencyKey();
  }
};

/** Oublie la clé persistée (422 métier / corps différent = nouvelle tentative). */
export const clearPersistedIdempotencyKey = (
  scopeId: string,
  fingerprint: string
): void => {
  try {
    sessionStorage.removeItem(storageKeyFor(scopeId, fingerprint));
  } catch {
    /* sessionStorage indisponible (mode privé strict, tests) */
  }
};
